"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { LogOut, Shield, ArrowLeft } from "lucide-react";
import { clearAdminStatusCache, getCachedAdminStatus, setCachedAdminStatus } from "@/lib/admin/useAdminStatus";

/**
 * AdminModeIndicator
 * 
 * Displays a persistent banner when an admin session is active.
 * Shows on all public pages (homepage, thread pages) to remind admin
 * they're in moderator mode and provides quick logout access.
 * 
 * Shows on admin pages as well so the "admin mode active" state is
 * consistently visible across navigation.
 */
export function AdminModeIndicator() {
  const pathname = usePathname();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isAdminPage = pathname?.startsWith("/admin");

  useEffect(() => {
    const checkAdminSession = async () => {
      // Check cache first
      const cached = getCachedAdminStatus();
      if (cached !== null) {
        setIsAdmin(cached);
        return;
      }

      // Cache miss on admin pages - make API call
      try {
        const response = await fetch("/api/admin/check", {
          method: "GET",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          const isAdminResult = data.authenticated === true;
          setIsAdmin(isAdminResult);
          setCachedAdminStatus(isAdminResult);
        } else {
          setIsAdmin(false);
          setCachedAdminStatus(false);
        }
      } catch {
        setIsAdmin(false);
        setCachedAdminStatus(false);
      }
    };

    checkAdminSession();
  }, [isAdminPage, pathname]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const response = await fetch("/api/admin/logout", {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        setIsAdmin(false);
        clearAdminStatusCache(); // Clear cache on logout
        // Full page navigation to reset all client-side state (including useAdminStatus hooks)
        window.location.href = isAdminPage ? "/" : window.location.href;
      }
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Don't render anything if not in admin mode
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-amber-500/90 to-orange-500/90 shadow-md">
      <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-2">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-black/80" />
          <span className="text-sm font-medium text-black/90">
            Logged in as Moderator
          </span>
          <span className="hidden text-xs text-black/60 sm:inline">
            · Admin mode active
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isAdminPage && (
            <Link
              href="/admin"
              className="flex items-center gap-1.5 rounded-full bg-black/20 px-3 py-1 text-sm font-medium text-black/90 transition hover:bg-black/30"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Dashboard
            </Link>
          )}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex items-center gap-1.5 rounded-full bg-black/20 px-3 py-1 text-sm font-medium text-black/90 transition hover:bg-black/30 disabled:opacity-50"
          >
            <LogOut className="h-3.5 w-3.5" />
            {isLoggingOut ? "..." : "Logout"}
          </button>
        </div>
      </div>
    </div>
  );
}
